// API Types based on Wabisabi API.yaml schema

// Error response
export interface ErrorResponse {
  error: string;
}

// Waitlist types
export interface WaitlistCreateRequest {
  email: string;
  referral_code?: string;
}

export interface WaitlistResponse {
  message: string;
  email: string;
  task_id?: string;
  referral_code?: string;
  referral_url?: string; // Optional - we generate this on frontend
}

export interface WaitlistStatus {
  email: string;
  in_waitlist: boolean;
}

export interface WaitlistUnsubscribeRequest {
  email: string;
}

// User types
export interface UserDetails {
  id: number;
  supabase_id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  country: string;
  avatar?: string;
  created_at: string;
  updated_at: string;
  completedOnboarding: boolean;
  onboarding_step: number;
}

// Contact types
export type RelationshipTypeEnum =
  | 'mother'
  | 'father'
  | 'sister'
  | 'brother'
  | 'spouse'
  | 'child'
  | 'friend'
  | 'colleague'
  | 'other';

export interface Contact {
  id: number;
  name: string;
  country?: string;
  birthday?: string | null;
  email?: string;
  phone?: string;
  avatar?: string;
  relationship_type: string;
  relationship: string;
  custom_data?: any;
  created_at: string;
  updated_at: string;
}

export interface ContactCreateUpdateRequest {
  name: string;
  country?: string;
  birthday?: string | null;
  email?: string;
  phone?: string;
  avatar?: string;
  relationship_type?: RelationshipTypeEnum | null;
  relationship_data?: any | null;
  custom_data?: any;
}

export interface PaginatedContactList {
  count: number;
  next: string | null;
  previous: string | null;
  results: Contact[];
}

export interface ContactLimits {
  current_count: number;
  limit: number;
  can_create: boolean;
  remaining: number;
  message: string;
}

// Contact Log types
export interface ContactLog {
  id: number;
  contact: number;
  contact_name: string;
  contact_avatar?: string;
  content: string;
  location?: string;
  mood?: string;
  log_date: string;
  created_at: string;
  updated_at: string;
  tags?: string[];
}

export interface ContactLogRequest {
  contact: number;
  content: string;
  location?: string;
  mood?: string;
}

export interface PaginatedContactLogList {
  count: number;
  next: string | null;
  previous: string | null;
  results: ContactLog[];
}

// Suggestion types
export type SuggestionTypeEnum =
  | 'activity'
  | 'gift'
  | 'reminder'
  | 'conversation'
  | 'checkin'
  | 'other';

export type StatusEnum =
  | 'pending'
  | 'scheduled'
  | 'completed'
  | 'dismissed'
  | 'expired';

export type PriorityEnum = 1 | 2 | 3; // Low, Medium, High

export interface Suggestion {
  id: number;
  contact: number;
  contact_name: string;
  contact_avatar?: string;
  user: number;
  title: string;
  content: string;
  suggestion_type: SuggestionTypeEnum;
  status: StatusEnum;
  priority: PriorityEnum;
  relevance_score?: number;
  source_logs: number[];
  source_logs_count: number;
  scheduled_for?: string | null;
  expires_at?: string | null;
  completed_at?: string | null;
  completion_notes?: string;
  user_rating?: number | null;
  ai_context?: any;
  feedback_count: number;
  created_at: string;
  updated_at: string;
}

export interface PaginatedSuggestionList {
  count: number;
  next: string | null;
  previous: string | null;
  results: Suggestion[];
}

// Notification preferences types
export interface NotificationPreferences {
  enabled: boolean;
  welcome_emails: boolean;
  first_log_emails: boolean;
  suggestion_emails: boolean;
  followup_emails: boolean;
  inactive_reminder_emails: boolean;
  new_user_nudge_emails: boolean;
  created_at: string;
  updated_at: string;
}

export interface NotificationPreferencesUpdate {
  enabled?: boolean;
  welcome_emails?: boolean;
  first_log_emails?: boolean;
  suggestion_emails?: boolean;
  followup_emails?: boolean;
  inactive_reminder_emails?: boolean;
  new_user_nudge_emails?: boolean;
}

// API endpoints
export const API_ENDPOINTS = {
  waitlist: {
    create: '/api/waitlist/',
    checkStatus: '/api/waitlist/check_status/',
    unsubscribe: '/api/waitlist/unsubscribe/',
  },
  users: {
    me: '/api/users/me',
  },
  contacts: {
    list: '/api/contacts/',
    detail: (id: number) => `/api/contacts/${id}/`,
    birthdays: '/api/contacts/birthdays/',
    byRelationship: '/api/contacts/by_relationship/',
    relationshipTypes: '/api/contacts/relationship_types/',
    search: '/api/contacts/search/',
    limits: '/api/contacts/limits/',
  },
  contactLogs: {
    list: '/api/contact-logs/',
    detail: (id: number) => `/api/contact-logs/${id}/`,
    byContact: '/api/contact-logs/by_contact/',
  },
  suggestions: {
    list: '/api/suggestions/',
    detail: (id: number) => `/api/suggestions/${id}/`,
    complete: (id: number) => `/api/suggestions/${id}/complete/`,
    dismiss: (id: number) => `/api/suggestions/${id}/dismiss/`,
    rate: (id: number) => `/api/suggestions/${id}/rate/`,
  },
  notifications: {
    preferences: '/api/notifications/preferences',
  },
} as const;